import React, { useState, useEffect } from 'react';
import axios from '../api/axios';
import './AccountHeads.css';
import { toast } from '../components/Toast';

function AccountHeads() {
  const [accountHeads, setAccountHeads] = useState([]);
  const [filteredAccountHeads, setFilteredAccountHeads] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [selectedAccountHead, setSelectedAccountHead] = useState(null);
  const [successMessage, setSuccessMessage] = useState('');
  const [filterCategory, setFilterCategory] = useState('ALL');

  // Form state
  const [formData, setFormData] = useState({
    name: '',
    category: 'EXPENSE',
    description: '',
    active: true
  });

  useEffect(() => {
    fetchAccountHeads();
  }, []);

  useEffect(() => {
    filterAccountHeads();
  }, [accountHeads, searchTerm, filterCategory]);

  const fetchAccountHeads = async () => {
    setLoading(true);
    setError('');
    try {
      const response = await axios.get('/account-heads');
      const data = response.data.data || response.data;
      setAccountHeads(data);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to fetch account heads');
    } finally {
      setLoading(false);
    }
  };

  const filterAccountHeads = () => {
    let filtered = [...accountHeads];

    if (filterCategory !== 'ALL') {
      filtered = filtered.filter(ah => ah.category === filterCategory);
    }

    if (searchTerm) {
      filtered = filtered.filter(ah =>
        ah.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (ah.description && ah.description.toLowerCase().includes(searchTerm.toLowerCase()))
      );
    }

    setFilteredAccountHeads(filtered);
  };

  const handleCreate = () => {
    setSelectedAccountHead(null);
    setFormData({
      name: '',
      category: 'EXPENSE',
      description: '',
      active: true
    });
    setShowModal(true);
  };

  const handleEdit = (accountHead) => {
    setSelectedAccountHead(accountHead);
    setFormData({
      name: accountHead.name,
      category: accountHead.category,
      description: accountHead.description || '',
      active: accountHead.active
    });
    setShowModal(true);
  };

  const handleModalClose = () => {
    setShowModal(false);
    setSelectedAccountHead(null);
    setFormData({
      name: '',
      category: 'EXPENSE',
      description: '',
      active: true
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    try {
      if (selectedAccountHead) {
        await axios.put(`/account-heads/${selectedAccountHead.id}`, formData);
        showSuccessMessage('Account head updated successfully');
      } else {
        await axios.post('/account-heads', formData);
        showSuccessMessage('Account head created successfully');
      }
      handleModalClose();
      fetchAccountHeads();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to save account head');
    }
  };

  const handleDelete = async (accountHead) => {
    if (!window.confirm(`Are you sure you want to deactivate "${accountHead.name}"?`)) {
      return;
    }

    try {
      await axios.delete(`/account-heads/${accountHead.id}`);
      fetchAccountHeads();
      showSuccessMessage('Account head deactivated successfully');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to deactivate account head');
    }
  };

  const showSuccessMessage = (message) => {
    toast.success(message);
  };

  const getCategoryBadgeClass = (category) => {
    switch (category) {
      case 'INCOME': return 'badge-income';
      case 'EXPENSE': return 'badge-expense';
      case 'PAYMENT_ACCOUNT': return 'badge-payment';
      default: return 'badge-default';
    }
  };

  const getCategoryLabel = (category) => {
    switch (category) {
      case 'INCOME': return 'Income';
      case 'EXPENSE': return 'Expense';
      case 'PAYMENT_ACCOUNT': return 'Payment Account';
      default: return category;
    }
  };

  return (
    <div className="account-heads-container">
      <div className="page-header">
        <h1>Account Heads</h1>
        <button className="btn-primary" onClick={handleCreate}>
          + Add Account Head
        </button>
      </div>

      <div className="filters-section">
        <div className="search-box">
          <input
            type="text"
            placeholder="Search account heads..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>

        <div className="category-filter">
          <label>Category:</label>
          <select value={filterCategory} onChange={(e) => setFilterCategory(e.target.value)}>
            <option value="ALL">All Categories</option>
            <option value="INCOME">Income</option>
            <option value="EXPENSE">Expense</option>
            <option value="PAYMENT_ACCOUNT">Payment Account</option>
          </select>
        </div>
      </div>

      {loading ? (
        <div className="loading">Loading account heads...</div>
      ) : (
        <div className="table-container">
          <table className="data-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Category</th>
                <th>Description</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredAccountHeads.length === 0 ? (
                <tr>
                  <td colSpan="5" className="no-data">No account heads found</td>
                </tr>
              ) : (
                filteredAccountHeads.map((accountHead) => (
                  <tr key={accountHead.id}>
                    <td data-label="Name"><strong>{accountHead.name}</strong></td>
                    <td data-label="Category">
                      <span className={`badge ${getCategoryBadgeClass(accountHead.category)}`}>
                        {getCategoryLabel(accountHead.category)}
                      </span>
                    </td>
                    <td data-label="Description">{accountHead.description || '-'}</td>
                    <td data-label="Status">
                      <span className={`status-badge ${accountHead.active ? 'status-active' : 'status-inactive'}`}>
                        {accountHead.active ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td data-label="Actions">
                      <div className="action-buttons">
                        <button className="btn btn-sm btn-secondary" onClick={() => handleEdit(accountHead)} title="Edit">
                          ✏️ Edit
                        </button>
                        {accountHead.active && (
                          <button className="btn btn-sm btn-danger" onClick={() => handleDelete(accountHead)} title="Deactivate">
                            🗑️ Deactivate
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}

      {showModal && (
        <div className="modal-overlay" onClick={handleModalClose}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{selectedAccountHead ? 'Edit Account Head' : 'Add Account Head'}</h2>
              <button className="modal-close" onClick={handleModalClose}>×</button>
            </div>

            <form onSubmit={handleSubmit}>
              <div className="form-group">
                <label>Name *</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                  maxLength={100}
                />
              </div>

              <div className="form-group">
                <label>Category *</label>
                <select
                  value={formData.category}
                  onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                  required
                >
                  <option value="INCOME">Income</option>
                  <option value="EXPENSE">Expense</option>
                  <option value="PAYMENT_ACCOUNT">Payment Account</option>
                </select>
              </div>

              <div className="form-group">
                <label>Description</label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  rows={3}
                  maxLength={255}
                />
              </div>

              <div className="form-group checkbox-group">
                <label>
                  <input
                    type="checkbox"
                    checked={formData.active}
                    onChange={(e) => setFormData({ ...formData, active: e.target.checked })}
                  />
                  Active
                </label>
              </div>

              <div className="modal-actions">
                <button type="button" className="btn-secondary" onClick={handleModalClose}>
                  Cancel
                </button>
                <button type="submit" className="btn-primary">
                  {selectedAccountHead ? 'Update' : 'Create'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default AccountHeads;
